import type {
    EvlConnectionSuccessPayload,
    EvlMessage,
    ExpertWorkspaceWebSocket,
    HandshakeUuidPayload,
} from "@expert/websocket";

import { getLogger } from "@expert/logging";
import { expertWorkspaceWebSocketEventBus } from "@expert/websocket";

import { sdkEventBus } from "@/sdk";

import type { CallSdk } from "./callSdk";

const logger = getLogger({ module: "evlCallSdk" });

export class EvlCallSdk implements CallSdk {
    private uuid: string | undefined;
    private isReady: boolean;

    private readonly onReady: (isReady: boolean) => void;
    private readonly webSocket: ExpertWorkspaceWebSocket;

    constructor(onReady: (isReady: boolean) => void, webSocket: ExpertWorkspaceWebSocket) {
        this.onReady = onReady;
        this.webSocket = webSocket;
        this.setupSubscriptions();
        this.isReady = false;
    }

    public updateAccessToken(_token: string): Promise<void> {
        throw new Error("TODO: Implement updateAccessToken");
    }

    public async sendDtmfDigits(digits: string): Promise<void> {
        this.webSocket.sendMessageToTenant("ExpertVoiceLink", "evl-message", {
            type: "dtmf-digits",
            digits,
        } as EvlMessage);
        await Promise.resolve();
    }

    private setupSubscriptions() {
        // NOTE: We send a request for the UUID here in case EVL is open already and has sent the UUID to ExWo while it was closed.
        //       In this case, ExWo would have missed the messages, so requesting it here makes sure we can launch EVL first and still
        //       connect properly.
        this.webSocket.sendMessageToTenant("ExpertVoiceLink", "request-uuid", {});

        expertWorkspaceWebSocketEventBus.on("ws_handshake-uuid", (payload: HandshakeUuidPayload) => {
            if (this.uuid !== payload.uuid) {
                if (this.uuid) {
                    logger.warn(
                        "EVL has sent a new UUID while workspace already had a UUID set. This could indicate that the expert has multiple workspaces open, or that they have an old session open that needs to be renewed.",
                    );
                }

                logger.info(`Echoing UUID '${payload.uuid}' back to EVL`);
                this.webSocket.sendMessageToTenant("ExpertVoiceLink", "handshake-uuid", {
                    uuid: payload.uuid,
                });
                this.uuid = payload.uuid;
            }
        });

        expertWorkspaceWebSocketEventBus.on("ws_evl-connection-success", (_: EvlConnectionSuccessPayload) => {
            if (!this.isReady) {
                this.onReady(true);
                this.isReady = true;
            } else {
                sdkEventBus.emit("evl_reconnected", true);
            }
        });

        expertWorkspaceWebSocketEventBus.on("ws_evl-connection-fail", (_: EvlConnectionSuccessPayload) => {
            sdkEventBus.emit("evl_disconnected", true);
        });
    }
}
